import { ComponentProps, ReactNode } from 'react';
import useIsMinimumMapHeight from '@alltrails/shared/hooks/useIsMinimumMapHeight';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import TooltipFor3dMap from '../TooltipFor3dMap';
import CompassControl from '../CompassControl';
import GeolocateControl from '../GeolocateControl';
import ZoomButtons from '../ZoomButtons';
import StyleControls from '../StyleControls';
import ControlContainer from '../ControlContainer';

export type Props = {
  additionalControls?: ReactNode;
  hideGeolocateControl?: boolean;
} & Pick<
  ComponentProps<typeof StyleControls>,
  'on3dButtonTapped' | 'onStyleModalChange' | 'onMapBaseLayerTapped' | 'onMapOverlayTapped' | 'disableAnalytics' | 'handleFailedEntitlementCheck'
>;

/**
 * @deprecated Use new MapControls component
 */
const BaseControls = ({
  additionalControls,
  hideGeolocateControl = false,
  on3dButtonTapped,
  onStyleModalChange,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleFailedEntitlementCheck
}: Props) => {
  const isMobile = useIsMobileSizedScreen();
  const isMinimumMapHeight = useIsMinimumMapHeight();
  const languageRegionCode = useLanguageRegionCode();

  return (
    <>
      {/* Helpful hints for map users */}
      <TooltipFor3dMap />
      <ControlContainer position="bottomLeft">
        {!!additionalControls && additionalControls}
        {!isMobile && <ZoomButtons />}
        {/* The arrowhead icon that is used to show the blue dot for a user's current location */}
        {isMinimumMapHeight && !hideGeolocateControl && <GeolocateControl />}
      </ControlContainer>
      {isMinimumMapHeight && (
        <ControlContainer position="topRight">
          <StyleControls
            handleFailedEntitlementCheck={handleFailedEntitlementCheck}
            handleRoadblock={() => {
              const returnToUrl = window.location.pathname + window.location.search;
              window.location.assign(wrapUrlSafe(`/signup?returnTo=${returnToUrl}`, languageRegionCode));
            }}
            on3dButtonTapped={on3dButtonTapped}
            onStyleModalChange={onStyleModalChange}
            onMapBaseLayerTapped={onMapBaseLayerTapped}
            onMapOverlayTapped={onMapOverlayTapped}
            disableAnalytics={disableAnalytics}
          />

          {/* The compass icon that indicates the current map bearing/direction */}
          <CompassControl />
        </ControlContainer>
      )}
    </>
  );
};

export default BaseControls;
